// Module 1 — canonical unit library for the Simulation Workbench.
//
// Rules of the road
// -----------------
// • Every scenario parameter is STORED IN SI (metres, seconds, kilograms,
//   newtons, pascals, kelvins, radians, or dimensionless). The backend
//   is the source of truth and only accepts SI.
// • Converters go SI → display and display → SI. Round-trip must be
//   lossless within 1e-9 relative tolerance for every unit we expose.
// • Temperature is affine (°C/°F have offsets), everything else is
//   linear — the dispatch table below reflects that.
// • There is no dependency on a global "current units system". Each
//   <ParamField> asks for a specific display unit; the hook that knows
//   the user's preference picks the unit from the kind's defaults.
//
// When Modules 2–7 add new kinds (e.g. acceleration, volumetric flow),
// append to KINDS. Do not special-case inside callers.

export const KINDS = Object.freeze({
  length:        { si: 'm',     label: 'Length',      aliases: ['distance'] },
  altitude:      { si: 'm',     label: 'Altitude',    aliases: [] },
  speed:         { si: 'm/s',   label: 'Speed',       aliases: ['velocity'] },
  mass:          { si: 'kg',    label: 'Mass',        aliases: [] },
  force:         { si: 'N',     label: 'Force',       aliases: ['thrust'] },
  pressure:      { si: 'Pa',    label: 'Pressure',    aliases: [] },
  temperature:   { si: 'K',     label: 'Temperature', aliases: [] },
  angle:         { si: 'rad',   label: 'Angle',       aliases: [] },
  time:          { si: 's',     label: 'Time',        aliases: ['duration'] },
  dimensionless: { si: '',      label: 'Dimensionless', aliases: ['ratio', 'count'] },
});

// Linear converter: display = si * toFactor + toOffset; si = (display - toOffset) / toFactor.
// Temperature uses offset != 0, everything else uses offset == 0.
const UNITS = Object.freeze({
  // length
  m:   { kind: 'length',   toFactor: 1,           toOffset: 0 },
  km:  { kind: 'length',   toFactor: 0.001,       toOffset: 0 },
  cm:  { kind: 'length',   toFactor: 100,         toOffset: 0 },
  mm:  { kind: 'length',   toFactor: 1000,        toOffset: 0 },
  ft:  { kind: 'length',   toFactor: 3.280839895013123, toOffset: 0 },
  nmi: { kind: 'length',   toFactor: 1 / 1852,    toOffset: 0 },
  mi:  { kind: 'length',   toFactor: 1 / 1609.344, toOffset: 0 },

  // altitude — reuse length but expose separately so the UI can offer ft by default
  'm-alt':  { kind: 'altitude', toFactor: 1, toOffset: 0, renderAs: 'm' },
  'ft-alt': { kind: 'altitude', toFactor: 3.280839895013123, toOffset: 0, renderAs: 'ft' },
  'fl':     { kind: 'altitude', toFactor: 3.280839895013123 / 100, toOffset: 0, renderAs: 'FL' }, // flight level (hundreds of feet)

  // speed
  'm/s':  { kind: 'speed', toFactor: 1,          toOffset: 0 },
  'km/h': { kind: 'speed', toFactor: 3.6,        toOffset: 0 },
  'kt':   { kind: 'speed', toFactor: 1.9438444924406046, toOffset: 0 },
  'mph':  { kind: 'speed', toFactor: 2.2369362920544025, toOffset: 0 },
  'ft/s': { kind: 'speed', toFactor: 3.280839895013123, toOffset: 0 },

  // mass
  kg: { kind: 'mass', toFactor: 1,          toOffset: 0 },
  g:  { kind: 'mass', toFactor: 1000,       toOffset: 0 },
  lb: { kind: 'mass', toFactor: 2.2046226218487757, toOffset: 0 },
  oz: { kind: 'mass', toFactor: 35.27396194958041, toOffset: 0 },

  // force
  N:    { kind: 'force', toFactor: 1,          toOffset: 0 },
  kN:   { kind: 'force', toFactor: 0.001,      toOffset: 0 },
  lbf:  { kind: 'force', toFactor: 0.2248089430997105, toOffset: 0 },

  // pressure
  Pa:   { kind: 'pressure', toFactor: 1,              toOffset: 0 },
  hPa:  { kind: 'pressure', toFactor: 0.01,           toOffset: 0 },
  kPa:  { kind: 'pressure', toFactor: 0.001,          toOffset: 0 },
  bar:  { kind: 'pressure', toFactor: 1e-5,           toOffset: 0 },
  atm:  { kind: 'pressure', toFactor: 1 / 101325,     toOffset: 0 },
  inHg: { kind: 'pressure', toFactor: 1 / 3386.389,   toOffset: 0 },
  psi:  { kind: 'pressure', toFactor: 1 / 6894.757293168361, toOffset: 0 },

  // temperature (affine)
  K:  { kind: 'temperature', toFactor: 1,     toOffset: 0 },
  C:  { kind: 'temperature', toFactor: 1,     toOffset: -273.15 },
  F:  { kind: 'temperature', toFactor: 9 / 5, toOffset: -459.67 },

  // angle
  rad: { kind: 'angle', toFactor: 1,             toOffset: 0 },
  deg: { kind: 'angle', toFactor: 180 / Math.PI, toOffset: 0 },
  mil: { kind: 'angle', toFactor: 1018.591635788130, toOffset: 0 }, // NATO mil (6400/circle)

  // time
  s:   { kind: 'time', toFactor: 1,         toOffset: 0 },
  ms:  { kind: 'time', toFactor: 1000,      toOffset: 0 },
  min: { kind: 'time', toFactor: 1 / 60,    toOffset: 0 },
  h:   { kind: 'time', toFactor: 1 / 3600,  toOffset: 0 },

  // dimensionless
  '':      { kind: 'dimensionless', toFactor: 1,   toOffset: 0 },
  pct:     { kind: 'dimensionless', toFactor: 100, toOffset: 0 },
  '%':     { kind: 'dimensionless', toFactor: 100, toOffset: 0 },
  ratio:   { kind: 'dimensionless', toFactor: 1,   toOffset: 0 },
});

export const UNIT_IDS = Object.freeze(Object.keys(UNITS));

/**
 * List units available for a kind, with a user-facing label.
 * Callers use this to populate the inline unit selector inside <ParamField>.
 */
export function unitsForKind(kind) {
  return UNIT_IDS
    .filter((u) => UNITS[u].kind === kind)
    .map((u) => ({ id: u, label: UNITS[u].renderAs || u }));
}

/** Resolve a unit descriptor. Throws on unknown id — fail fast at the caller. */
function _descriptor(unit) {
  const d = UNITS[unit];
  if (!d) throw new Error(`units: unknown unit "${unit}"`);
  return d;
}

/** SI value → display unit. */
export function toDisplay(valueSI, unit) {
  if (valueSI === null || valueSI === undefined || Number.isNaN(valueSI)) return valueSI;
  const d = _descriptor(unit);
  return valueSI * d.toFactor + d.toOffset;
}

/** Display value → SI. */
export function fromDisplay(valueDisplay, unit) {
  if (valueDisplay === null || valueDisplay === undefined || Number.isNaN(valueDisplay)) {
    return valueDisplay;
  }
  const d = _descriptor(unit);
  return (valueDisplay - d.toOffset) / d.toFactor;
}

/** Convert between two units of the same kind, pivoting through SI. */
export function convert(value, fromUnit, toUnit) {
  if (fromUnit === toUnit) return value;
  const from = _descriptor(fromUnit);
  const to = _descriptor(toUnit);
  if (from.kind !== to.kind) {
    throw new Error(`units: cannot convert ${fromUnit} (${from.kind}) → ${toUnit} (${to.kind})`);
  }
  return toDisplay(fromDisplay(value, fromUnit), toUnit);
}

/** What kind does this unit belong to? */
export function kindOf(unit) {
  return _descriptor(unit).kind;
}

// Default unit-per-kind by system. Metric sticks to SI. Imperial picks the
// aviation-standard display unit for each kind (ft for altitude, kt for
// speed, lb for mass, lbf for force, inHg for pressure, °F for temperature).
export const SYSTEM_DEFAULTS = Object.freeze({
  metric: {
    length: 'm',        altitude: 'm-alt', speed: 'm/s',
    mass: 'kg',         force: 'N',        pressure: 'hPa',
    temperature: 'C',   angle: 'deg',      time: 's',
    dimensionless: '',
  },
  imperial: {
    length: 'ft',       altitude: 'ft-alt', speed: 'kt',
    mass: 'lb',         force: 'lbf',       pressure: 'inHg',
    temperature: 'F',   angle: 'deg',       time: 's',
    dimensionless: '',
  },
});

/** Return the preferred display unit for a kind under a system. */
export function defaultUnitFor(kind, system = 'metric') {
  const table = SYSTEM_DEFAULTS[system] || SYSTEM_DEFAULTS.metric;
  return table[kind] ?? UNITS[kind]?.si ?? '';
}

/** Formatter — stable string representation suitable for inputs. */
export function formatValue(valueSI, unit, { digits = 4 } = {}) {
  if (valueSI === null || valueSI === undefined || Number.isNaN(valueSI)) return '';
  const v = toDisplay(valueSI, unit);
  // Avoid exponential creep on small numbers; pad trailing zeros.
  if (Math.abs(v) >= 1e6 || (Math.abs(v) > 0 && Math.abs(v) < 1e-3)) {
    return v.toExponential(Math.max(0, digits - 1));
  }
  return Number(v.toFixed(digits)).toString();
}
